import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { fromPlain } from "@/lib/fieldCodec";
import { logEdit } from "./audit";

export function NewDocDialog({ path, open, onClose, onCreated }) {
  const { user } = useAuth();
  const [id, setId] = useState("");
  const [body, setBody] = useState('{\n  "created_at": { "__timestamp": "' + new Date().toISOString() + '" }\n}');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    let data;
    try { data = JSON.parse(body); } catch { return toast.error("Invalid JSON"); }
    if (!data || typeof data !== "object" || Array.isArray(data)) return toast.error("Body must be a JSON object");
    setBusy(true);
    try {
      if (id.trim() && await adapter.get(path, id.trim())) { toast.error("A document with this ID already exists"); return; }
      const newId = await adapter.createDoc(path, id.trim() || null, fromPlain(data));
      await logEdit(user, { path, id: newId, action: "create_document", after: data, sensitive: false });
      toast.success(`Created ${path}/${newId}`);
      onCreated(newId);
    } catch (e) { toast.error(e.code === "permission-denied" ? "Permission denied by Firestore rules" : e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New document in {path}</DialogTitle>
          <DialogDescription>Leave ID blank for an auto-ID. Timestamps: {'{"__timestamp": "ISO"}'}; geo: {'{"__geopoint": [lat, lng]}'}.</DialogDescription>
        </DialogHeader>
        <Input value={id} onChange={(e) => setId(e.target.value)} placeholder="Document ID (optional)" className="font-mono" data-testid="new-doc-id" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="font-mono text-xs" spellCheck={false} data-testid="new-doc-body" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="new-doc-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="new-doc-submit">{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
